import React, { useState, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Video, Upload, Library, Camera, Film, Loader2, Radio } from 'lucide-react';

// Lazy-load each tab's existing page component
const VideoAnalyticsPage = lazy(() => import('./VideoAnalyticsPage'));
const DropZonePage = lazy(() => import('./DropZonePage'));
const MyLibraryPage = lazy(() => import('../pages/basketball/MyLibraryPage'));
const SlamMobile = lazy(() => import('../pages/basketball/SlamMobile'));
const LiveVideoTab = lazy(() => import('../components/video/LiveVideoTab'));

const TABS = [
  { id: 'analyze', label: 'Analyze', icon: Video, description: 'Upload and analyze video' },
  { id: 'upload', label: 'Batch Upload', icon: Upload, description: 'Batch game footage analysis' },
  { id: 'library', label: 'My Library', icon: Library, description: 'Browse processed videos' },
  { id: 'practice', label: 'Practice Film', icon: Camera, description: 'Real-time biomechanics' },
  { id: 'live', label: 'Live', icon: Radio, description: 'Real-time AI analysis via camera or screen' },
];

const TAB_COMPONENTS = {
  analyze: VideoAnalyticsPage,
  upload: DropZonePage,
  library: MyLibraryPage,
  practice: SlamMobile,
  live: LiveVideoTab,
};

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64 text-gray-400">
    <Loader2 className="animate-spin mr-2" size={20} />
    Loading...
  </div>
);

export default function VideoHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'analyze';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };

  const ActiveComponent = TAB_COMPONENTS[activeTab] || VideoAnalyticsPage;

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-gray-700/50 bg-gray-900/30 px-4">
        <div className="flex items-center gap-1 -mb-px overflow-x-auto">
          <Film size={18} className="text-blue-400 mr-2 flex-shrink-0" />
          <span className="text-sm font-semibold text-white mr-4 flex-shrink-0">Video Hub</span>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
                title={tab.description}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        <Suspense fallback={<LoadingFallback />}>
          <ActiveComponent />
        </Suspense>
      </div>
    </div>
  );
}
